/**
 * Changing a trip's dates after it exists, and moving what hangs off them.
 *
 * The date fields lived only on the create form, so a host could never change
 * them, and a reminder plan's "Reschedule plan" (which fires when the trip's
 * start date no longer matches the date the plan was built for) was unreachable
 * from the UI. This module is the pure half of the Overview "Trip dates" card:
 * validation, the day arithmetic, and the plan for shifting the itinerary and
 * the dinner holds by the same number of days. The card and its dialog wire it
 * to the network; nothing here sends anything to a guest.
 *
 * Why the shift is done on the WALL CLOCK, not by adding 24 h to an instant:
 * "Fri Oct 23, 8:00 PM" in Lisbon moved two days is "Sun Oct 25, 8:00 PM", but
 * Lisbon leaves summer time at 02:00 on Oct 25, so adding 48 h to the instant
 * lands at 7:00 PM. The local time is the host's intent, so the date part of the
 * wall clock moves and the offset is looked up again at the result (tripTime).
 *
 * Writes go through the existing helpers and nothing new is invented:
 *   - an itinerary event is saved through `itineraryToPayload` (the upsert
 *     assigns most columns outright, so a partial body blanks the rest);
 *   - a dinner is saved through `buildDinnerPatch` (time and zone travel
 *     together, and `expectedSourceVersion` makes a concurrent edit a 409).
 */
import { itineraryToPayload } from './groupPayload';
import { plural, pluralWord } from './plural';
import { addDaysYmd, daysBetweenYmd, detectPlanDateDrift, groupScheduledBySequence, normalizeTripDate } from './reminderPlan';
import { buildDinnerPatch, dinnerEditZone, formFromHold, type DinnerPatch } from './dinnerEdit';
import { clockOf, itemChangedSince, stamp } from './rezoneItinerary';
import { isoToWallClock, isUsableTimeZone, wallClockToIso } from './tripTime';
import type { GroupDinnerHold, GroupItineraryItem, GroupScheduledBroadcast } from '../types/groups';

// ---------------------------------------------------------------------------
// The two dates
// ---------------------------------------------------------------------------

export interface TripDatesDraft {
  /** `YYYY-MM-DD` from a date input, '' when empty. */
  start: string;
  end: string;
}

export interface TripDatesErrors {
  start?: string;
  end?: string;
}

const day = (value: string) => normalizeTripDate(value);

/** The card's starting draft: the saved dates, as the date input wants them. */
export function draftFromGroup(group: { dateStart?: string | null; dateEnd?: string | null }): TripDatesDraft {
  return { start: day(group.dateStart ?? '') ?? '', end: day(group.dateEnd ?? '') ?? '' };
}

const yearOf = (ymd: string) => Number(ymd.slice(0, 4));

/** A typed year more than this far from the SAVED one is unfinished input while typing (Chrome's date input emits 0002, 0020, 0202 on the way to 2027). Used only to decide whether the end follows the start. */
export const YEAR_WINDOW = 5;
const withinSavedWindow = (ymd: string, saved: string): boolean => Math.abs(yearOf(ymd) - yearOf(saved)) <= YEAR_WINDOW;

/** A COUNT for a sentence, with thousands separators ("666,570"). Never for a year: those are printed bare (2046). */
const count = (n: number): string => n.toLocaleString('en-US');

/** Years from this many before today to this many after pass without a question; anything else needs the host to say it is meant. */
export const YEARS_BEFORE_TODAY = 2;
export const YEARS_AFTER_TODAY = 10;
/** The longest trip the card saves without asking (a trip already longer than this may keep its own length). */
export const MAX_TRIP_DAYS = 60;

/** The year, in the clock the host sees. Only the card calls it; every function below takes it as a parameter so tests need no clock. */
export const thisYear = (): number => new Date().getFullYear();

/**
 * Both dates are required once either is set, the end cannot be before the
 * start, and a saved date can be changed but not removed (the update endpoint
 * keeps a stored date when none is sent, so "clear" would silently do nothing).
 *
 * A year below 1000 is HARD-blocked ("Finish typing the year."): a date input passes through 0002, 0020 and 0202 on the way to
 * 2027, and those are never a trip. Everything merely unusual (a far year, a very long trip) is a question, not an error: see
 * `tripDatesWarning`. Plausibility is judged against TODAY, never the saved year, so a wrong saved year can always be fixed here.
 */
export function validateTripDates(draft: TripDatesDraft, saved: TripDatesDraft): TripDatesErrors {
  const errors: TripDatesErrors = {};
  const start = draft.start.trim();
  const end = draft.end.trim();
  if (!start && !end) {
    if (saved.start || saved.end) errors.start = 'A trip date can be changed here, but not removed.';
    return errors;
  }
  // A year under 1000 may not even parse as a date (0002 reads as 1902), so it is recognised before the "real date" check.
  const unfinished = (value: string) => /^\d{4}-/.test(value) && Number(value.slice(0, 4)) < 1000;
  if (!start) errors.start = 'Add a start date.';
  else if (unfinished(start)) errors.start = 'Finish typing the year.';
  else if (!day(start)) errors.start = 'Enter a real date.';
  if (!end) errors.end = 'Add an end date.';
  else if (unfinished(end)) errors.end = 'Finish typing the year.';
  else if (!day(end)) errors.end = 'Enter a real date.';
  if (!errors.start && !errors.end && (day(end) as string) < (day(start) as string)) {
    errors.end = "The end date can't be before the start date.";
  }
  return errors;
}

/**
 * What is unusual about dates that are otherwise valid, as ONE question for the host, or null when nothing is. Save stays off
 * until they say they mean it ("Use these dates anyway"). Only a YEAR the host changed is judged (a wrong saved year
 * must not nag about an end date edited beside it), against today: currentYear - 2 to currentYear + 10 pass.
 */
export function tripDatesWarning(draft: TripDatesDraft, saved: TripDatesDraft, currentYear: number = thisYear()): string | null {
  if (Object.keys(validateTripDates(draft, saved)).length > 0 || !draft.start.trim() || !draft.end.trim()) return null;
  const start = day(draft.start) as string;
  const end = day(draft.end) as string;
  const far = (year: number) => year < currentYear - YEARS_BEFORE_TODAY || year > currentYear + YEARS_AFTER_TODAY;
  const questions: string[] = [];
  // One entry per date whose YEAR the host changed to a far one. Only a changed year is questioned: nudging the day inside a (wrong or odd) saved year is not a new year.
  const farYear = (ymd: string, savedYmd: string) => {
    const before = day(savedYmd);
    return far(yearOf(ymd)) && !(before && yearOf(before) === yearOf(ymd)) ? { year: yearOf(ymd), before: before ? yearOf(before) : null } : null;
  };
  // Years are printed bare (2046, never "2,046"); a COUNT of years or days gets thousands separators ("666,570 days").
  const sentence = (who: string, entry: { year: number; before: number | null }) => (entry.before !== null
    ? `The ${who} ${who.includes(' and ') ? 'years go' : 'year goes'} ${entry.before} → ${entry.year}, ${count(Math.abs(entry.year - entry.before))} ${pluralWord(Math.abs(entry.year - entry.before), 'year')}, and ${entry.year} is a long way from today.`
    : `${entry.year} is a long way from today.`);
  const startFar = farYear(start, saved.start);
  const endFar = farYear(end, saved.end);
  if (startFar && endFar && startFar.year === endFar.year && startFar.before === endFar.before) {
    questions.push(sentence('start and end', startFar));                 // the same far year for both is one sentence, not two
  } else {
    if (startFar) questions.push(sentence('start', startFar));
    if (endFar) questions.push(sentence('end', endFar));
  }
  const length = daysBetweenYmd(start, end) + 1;
  const savedStart = day(saved.start);
  const savedEnd = day(saved.end);
  const allowed = Math.max(MAX_TRIP_DAYS, savedStart && savedEnd ? daysBetweenYmd(savedStart, savedEnd) + 1 : 0);
  if (length > allowed) questions.push(`That makes the trip ${count(length)} days long.`);
  return questions.length > 0 ? `${questions.join(' ')} Use these dates anyway?` : null;
}

export function tripDatesDirty(draft: TripDatesDraft, saved: TripDatesDraft): boolean {
  return draft.start.trim() !== saved.start || draft.end.trim() !== saved.end;
}

/**
 * Save needs a change and a valid pair; and if the dates are unusual (`tripDatesWarning`), the host's yes to that exact question
 * (`confirmed` is the question they ticked, so changing a date after ticking asks again).
 */
export function canSaveTripDates(
  draft: TripDatesDraft,
  saved: TripDatesDraft,
  confirmed: string | null = null,
  currentYear: number = thisYear(),
): boolean {
  const errors = validateTripDates(draft, saved);
  if (!tripDatesDirty(draft, saved) || errors.start || errors.end || !draft.start.trim() || !draft.end.trim()) return false;
  const question = tripDatesWarning(draft, saved, currentYear);
  return question === null || confirmed === question;
}

/**
 * The same checks for a trip that does not exist yet (the create form, including dates the event import filled in): nothing is
 * saved, so every year is judged against today. Both dates are optional there, as they always were; with only one given it is
 * checked on its own. `errors` are hard (an unfinished year, an end before the start); `question` is the one soft "use these dates
 * anyway?" for a far year or a trip over 60 days.
 */
export function checkNewTripDates(
  dates: { dateStart?: string | null; dateEnd?: string | null },
  currentYear: number = thisYear(),
): { errors: TripDatesErrors; question: string | null } {
  const start = (dates.dateStart ?? '').trim();
  const end = (dates.dateEnd ?? '').trim();
  const empty: TripDatesDraft = { start: '', end: '' };
  if (!start && !end) return { errors: {}, question: null };
  // With one date, judge it as a one-day trip and report on the field it came from.
  const judged: TripDatesDraft = { start: start || end, end: end || start };
  const errors = validateTripDates(judged, empty);
  if (!start) delete errors.start;
  if (!end) delete errors.end;
  return { errors, question: tripDatesWarning(judged, empty, currentYear) };
}

/** Whole days the start moved (positive = later); null when either date is missing. */
export function startShiftDays(oldStart: string | null | undefined, newStart: string | null | undefined): number | null {
  const from = day(oldStart ?? '');
  const to = day(newStart ?? '');
  return from && to ? daysBetweenYmd(from, to) : null;
}

/**
 * The draft after the START is edited. An end date the host has not touched keeps its distance from the start, so moving the
 * start does not silently shorten the trip; once the host edits the end it is theirs.
 *
 * Always worked out from the SAVED dates, never from the previous draft: a date input emits every intermediate value while a year is
 * typed (0002, 0020, 0202, 2027), and shifting from one to the next compounds into an end date centuries away. A start whose year is
 * not within YEAR_WINDOW of the saved one is unfinished input, so the end is left alone (a year under 1000 is also refused by validateTripDates).
 */
export function draftAfterStartEdit(
  saved: TripDatesDraft,
  prev: TripDatesDraft,
  newStart: string,
  endTouched: boolean,
): { draft: TripDatesDraft; endFollowed: boolean } {
  const savedStart = day(saved.start);
  const savedEnd = day(saved.end);
  const start = day(newStart);
  const stay = { draft: { ...prev, start: newStart }, endFollowed: false };
  if (endTouched || !savedStart || !savedEnd || !start || !withinSavedWindow(start, savedStart)) return stay;
  const days = daysBetweenYmd(savedStart, start);
  if (days === 0) return { draft: { start: newStart, end: savedEnd }, endFollowed: false };
  return { draft: { start: newStart, end: addDaysYmd(savedEnd, days) }, endFollowed: true };
}

/**
 * How many reminder plans would now offer "Reschedule plan" for this start date: the same test the Broadcasts tab applies
 * (`detectPlanDateDrift`: the plan's stored anchor differs from the start date and a step is still pending). Zero means the
 * note about it would be talking about nothing.
 */
export function plansOfferingReschedule(rows: readonly GroupScheduledBroadcast[] | null | undefined, newStart: string): number {
  return groupScheduledBySequence([...(rows || [])]).filter((item) => (
    item.kind === 'plan' && detectPlanDateDrift(item.rows, newStart, '').drifted
  )).length;
}

// ---------------------------------------------------------------------------
// Moving an instant by whole days, in a zone
// ---------------------------------------------------------------------------

export interface ShiftedInstant {
  iso: string;
  fromWall: string;
  toWall: string;
  /** The local time the shift asked for. */
  wantedWall: string;
  /** True when the shifted local time does not exist that day (a spring-forward gap), so `toWall` is not `wantedWall`. */
  adjusted: boolean;
}

/**
 * The same local time `days` calendar days away, in `zone`. Null when the instant
 * or the zone cannot be read. DST-safe: see the header.
 */
export function shiftInstantByDays(iso: string | null | undefined, days: number, zone: string): ShiftedInstant | null {
  if (!iso || !isUsableTimeZone(zone)) return null;
  const date = new Date(iso);
  const fromWall = isoToWallClock(iso, zone);
  if (Number.isNaN(date.getTime()) || !fromWall) return null;
  const [ymd, clock] = fromWall.split('T');
  const wanted = `${addDaysYmd(ymd, days)}T${clock}`;
  // Zone offsets are whole minutes, so the UTC seconds ARE the local seconds; carry them across.
  const seconds = String(date.getUTCSeconds()).padStart(2, '0');
  const moved = wallClockToIso(`${wanted}:${seconds}`, zone);
  if (!moved) return null;
  const toWall = isoToWallClock(moved, zone);
  return { iso: moved, fromWall, toWall, wantedWall: wanted, adjusted: toWall !== wanted };
}

// ---------------------------------------------------------------------------
// The plan: what could move, and what each becomes
// ---------------------------------------------------------------------------

/**
 * What the host reads when a shifted time does not exist that day, from what actually happened:
 * "Becomes 1:30 AM — 2:30 AM doesn't exist that day." (An event with both ends names each that moved.)
 */
export function gapNote(parts: ReadonlyArray<{ label?: string; shifted: ShiftedInstant | null }>): string | null {
  const hit = parts.filter((part): part is { label?: string; shifted: ShiftedInstant } => Boolean(part.shifted?.adjusted));
  if (hit.length === 0) return null;
  const clauses = hit.map(({ label, shifted }) => (
    `${label ?? 'Becomes'} ${clockOf(shifted.toWall)} — ${clockOf(shifted.wantedWall)} doesn't exist that day`
  ));
  return `${clauses.join('; ')}.`;
}

export interface EventShift {
  kind: 'event';
  id: string;
  item: GroupItineraryItem;
  /** The zone this event is read in (its own, else the trip's). */
  zone: string;
  startsAt: string | null;
  endsAt: string | null;
  fromWall: string;
  toWall: string;
  adjusted: boolean;
  /** Set when `adjusted`: what actually happened to the time, in words. */
  gapNote: string | null;
  /** The result still lands outside the trip's dates (only judged when `planDateShift` is given the dates). */
  stillOutside: boolean;
  label: string;
  /** "Welcome drinks: Fri, Oct 16, 8:00 PM → Sat, Oct 17, 8:00 PM (Europe/Lisbon)" */
  summary: string;
}

export interface DinnerShift {
  kind: 'dinner';
  id: string;
  hold: GroupDinnerHold;
  zone: string;
  reservedAt: string;
  fromWall: string;
  toWall: string;
  adjusted: boolean;
  gapNote: string | null;
  stillOutside: boolean;
  /** Guests holding seats (the party sizes summed): moving the dinner changes the night they have been promised. */
  claimedSpots: number;
  label: string;
  summary: string;
  /** The exact PATCH: reservedAt + timeZone together and `expectedSourceVersion`. */
  patch: DinnerPatch;
}

export type DateShiftMove = EventShift | DinnerShift;

export interface DateShiftSkip {
  label: string;
  reason: 'cancelled' | 'unreadable' | 'range_inverts';
}

export interface DateShiftPlan {
  /** Whole days every move shifts by. */
  days: number;
  events: EventShift[];
  dinners: DinnerShift[];
  skipped: DateShiftSkip[];
}

const clean = (value?: string | null) => (value || '').trim();
const arrow = (zone: string, from: string, to: string) => `${stamp(from, true)} → ${stamp(to, true)} (${zone})`;
const byWall = (a: { fromWall: string }, b: { fromWall: string }) => a.fromWall.localeCompare(b.fromWall);

export function planEvent(item: GroupItineraryItem, days: number, tripZone: string): EventShift | DateShiftSkip | null {
  if (!item.startsAt && !item.endsAt) return null;           // nothing a date changes the reading of
  const label = clean(item.title) || 'Untitled event';
  // A cancelled tile is retained, audited history (see rezoneItinerary); it is reported, not rewritten.
  if ((item.status || '').toLowerCase() === 'cancelled') return { label, reason: 'cancelled' };
  const zone = clean(item.timeZone) || tripZone;
  const start = item.startsAt ? shiftInstantByDays(item.startsAt, days, zone) : null;
  const end = item.endsAt ? shiftInstantByDays(item.endsAt, days, zone) : null;
  if ((item.startsAt && !start) || (item.endsAt && !end)) return { label, reason: 'unreadable' };
  if (start && end && Date.parse(end.iso) < Date.parse(start.iso)) return { label, reason: 'range_inverts' };
  const anchor = (start || end)!;
  return {
    kind: 'event',
    id: item.id,
    item,
    zone,
    startsAt: start?.iso ?? null,
    endsAt: end?.iso ?? null,
    fromWall: anchor.fromWall,
    toWall: anchor.toWall,
    adjusted: Boolean((start && start.adjusted) || (end && end.adjusted)),
    stillOutside: false,
    gapNote: gapNote(start && end
      ? [{ label: 'Starts at', shifted: start }, { label: 'ends at', shifted: end }]
      : [{ shifted: start || end }]),
    label,
    summary: `${label}: ${arrow(zone, anchor.fromWall, anchor.toWall)}`,
  };
}

export function planDinner(hold: GroupDinnerHold, days: number, tripZone: string): DinnerShift | DateShiftSkip | null {
  if (!hold.reservedAt || (hold.status || '').toLowerCase() === 'cancelled') return null;
  const label = clean(hold.venueName) || 'Dinner';
  const zone = dinnerEditZone(hold, tripZone);
  const moved = shiftInstantByDays(hold.reservedAt, days, zone);
  if (!moved) return { label, reason: 'unreadable' };
  // Through the dinner edit path: time and zone together, and the version the host read.
  const patch = buildDinnerPatch(hold, { ...formFromHold(hold, zone), whenLocal: moved.toWall }, zone);
  if (!patch) return null;
  return {
    kind: 'dinner',
    id: hold.id,
    hold,
    zone,
    reservedAt: moved.iso,
    fromWall: moved.fromWall,
    toWall: moved.toWall,
    adjusted: moved.adjusted,
    stillOutside: false,
    gapNote: gapNote([{ shifted: moved }]),
    claimedSpots: Math.max(0, Number(hold.claimedSpots) || 0),
    label: `Dinner at ${label}`,
    summary: `Dinner at ${label}: ${arrow(zone, moved.fromWall, moved.toWall)}`,
    patch,
  };
}

/**
 * Which events and dinners would move `days` days, and what each becomes.
 * Pure: reads, changes nothing. `days` of 0 plans nothing.
 */
export function planDateShift(input: {
  items: readonly GroupItineraryItem[] | null | undefined;
  holds: readonly GroupDinnerHold[] | null | undefined;
  days: number;
  tripZone: string;
  /** The trip's dates: with them, each move whose result still lands outside is flagged `stillOutside`. */
  range?: { start: string; end: string };
}): DateShiftPlan {
  const plan: DateShiftPlan = { days: input.days, events: [], dinners: [], skipped: [] };
  if (!Number.isInteger(input.days) || input.days === 0) return plan;
  for (const item of input.items || []) {
    const planned = planEvent(item, input.days, input.tripZone);
    if (!planned) continue;
    if ('reason' in planned) plan.skipped.push(planned);
    else plan.events.push(planned);
  }
  for (const hold of input.holds || []) {
    const planned = planDinner(hold, input.days, input.tripZone);
    if (!planned) continue;
    if ('reason' in planned) plan.skipped.push(planned);
    else plan.dinners.push(planned);
  }
  const range = input.range && day(input.range.start) && day(input.range.end) ? input.range : null;
  if (range) {
    for (const move of [...plan.events, ...plan.dinners]) {
      const date = move.toWall.slice(0, 10);
      move.stillOutside = date < range.start || date > range.end;
    }
  }
  plan.events.sort(byWall);
  plan.dinners.sort(byWall);
  return plan;
}

/** Nothing is pre-selected: the host chooses what moves. An earlier review rejected all-ticked defaults. */
export function defaultShiftSelection(): ReadonlySet<string> {
  return new Set<string>();
}

/** Every move's id, for "Select all". Event and dinner ids live in separate tables, so the key carries the kind. */
export const shiftKey = (move: Pick<DateShiftMove, 'kind' | 'id'>) => `${move.kind}:${move.id}`;

/** What "Select all" ticks: every move EXCEPT one that would still land outside the trip dates (the host can tick those by hand). */
export const allShiftKeys = (plan: DateShiftPlan): Set<string> => (
  new Set([...plan.events, ...plan.dinners].filter((move) => !move.stillOutside).map(shiftKey))
);

/** The full upsert body for one event: through `itineraryToPayload`, never a bare { id, startsAt }. Its own zone is untouched. */
export function dateShiftPayload(move: EventShift): Record<string, unknown> {
  return itineraryToPayload(move.item, {
    startsAt: move.startsAt ?? undefined,
    endsAt: move.endsAt ?? undefined,
  });
}

/** "2 days later", "1 day earlier" */
export function shiftPhrase(days: number): string {
  const n = Math.abs(days);
  return `${n} ${n === 1 ? 'day' : 'days'} ${days > 0 ? 'later' : 'earlier'}`;
}

// ---------------------------------------------------------------------------
// Events that sit outside the trip's dates (the way back in after "Leave them as they are")
// ---------------------------------------------------------------------------

export interface OutsideTripRange {
  /** Non-cancelled events whose local date is before the trip starts or after it ends. */
  count: number;
  /** Only the rows outside the dates (events, and dinners when given): what the catch-up offers. Rows inside are never offered. */
  items: GroupItineraryItem[];
  holds: GroupDinnerHold[];
  /**
   * The shift that brings the OUTSIDE rows in: the earliest of those before the start lands on the start date (or, when all sit
   * after the end, the latest lands on the end date). Rows inside the dates play no part. Never 0.
   */
  suggestedDays: number;
}

/**
 * Itinerary events and dinners a date change left outside [dateStart, dateEnd], each read in its own zone (else the trip's).
 * Rows INSIDE the range are not returned: a catch-up that offered them would move a row that is already right (and a second
 * week for the 12 of 14 that a partial move had already shifted). A rehearsal dinner the day before the start is outside too, so
 * it is offered alone and the host decides.
 */
export function outsideTripRange(input: {
  items: readonly GroupItineraryItem[] | null | undefined;
  holds?: readonly GroupDinnerHold[] | null | undefined;
  dateStart: string | null | undefined;
  dateEnd: string | null | undefined;
  tripZone: string;
}): OutsideTripRange {
  const start = day(input.dateStart ?? '');
  const end = day(input.dateEnd ?? '');
  const none: OutsideTripRange = { count: 0, items: [], holds: [], suggestedDays: 1 };
  if (!start || !end) return none;
  const before: string[] = [];
  const after: string[] = [];
  const place = (date: string) => {
    if (!date) return false;
    if (date < start) before.push(date);
    else if (date > end) after.push(date);
    else return false;
    return true;
  };
  const items = (input.items || []).filter((item) => {
    if ((!item.startsAt && !item.endsAt) || (item.status || '').toLowerCase() === 'cancelled') return false;
    return place(isoToWallClock(item.startsAt || item.endsAt, clean(item.timeZone) || input.tripZone).slice(0, 10));
  });
  const holds = (input.holds || []).filter((hold) => {
    if (!hold.reservedAt || (hold.status || '').toLowerCase() === 'cancelled') return false;
    return place(isoToWallClock(hold.reservedAt, dinnerEditZone(hold, input.tripZone)).slice(0, 10));
  });
  if (before.length + after.length === 0) return none;
  const suggestedDays = before.length > 0
    ? daysBetweenYmd([...before].sort()[0], start)
    : daysBetweenYmd([...after].sort().reverse()[0], end);
  return { count: items.length + holds.length, items, holds, suggestedDays };
}

/** "2 itinerary events and 1 dinner sit outside these dates." (events and dinners both count: a left-behind dinner is as stranded as an event) */
export function outsidePhrase(found: Pick<OutsideTripRange, 'items' | 'holds'>): string {
  const total = found.items.length + found.holds.length;
  const parts = [
    found.items.length > 0 ? plural(found.items.length, 'itinerary event') : null,
    found.holds.length > 0 ? plural(found.holds.length, 'dinner') : null,
  ].filter(Boolean).join(' and ');
  return `${parts} ${pluralWord(total, 'sits', 'sit')} outside these dates.`;
}

// ---------------------------------------------------------------------------
// Running it
// ---------------------------------------------------------------------------

export interface DateShiftFailure {
  label: string;
  message: string;
  /** What was meant to move: the retry re-plans it from a fresh read. */
  move: DateShiftMove;
}

export interface DateShiftOutcome {
  movedEvents: EventShift[];
  movedDinners: DinnerShift[];
  failed: DateShiftFailure[];
}

/** The line the host reads for something that was not moved. */
export const failureLine = (failure: Pick<DateShiftFailure, 'label' | 'message'>) => `${failure.label}: ${failure.message}`;

/** Shown for an item the retry will not touch: someone moved it, so moving it again would be a second, unintended shift. */
/** Said of an event or dinner someone edited since the plan was made: the dialog's retry re-plans it, so no "reload". */
export const EDITED_MEANWHILE = 'was edited while this page was open';

export const CHANGED_ELSEWHERE = 'changed by someone else — check it on the Itinerary tab';

/**
 * Saves the ticked moves one at a time and keeps going past a failure, so one
 * refused item cannot strand the rest and the host learns exactly which landed.
 *
 * An event is compared with the server's copy first (`freshItems`): the backend
 * has no version on itinerary items and the upsert assigns most columns outright,
 * so saving from a stale screen would overwrite a co-host's edit. One that changed
 * or vanished is reported, not saved. A dinner needs no such read: its PATCH carries
 * `expectedSourceVersion`, and the server answers a change since as a 409.
 */
export async function runDateShift(input: {
  events: readonly EventShift[];
  dinners: readonly DinnerShift[];
  freshItems: readonly GroupItineraryItem[] | null | undefined;
  saveEvent: (move: EventShift) => Promise<unknown>;
  saveDinner: (move: DinnerShift) => Promise<unknown>;
  describeEventError: (error: unknown) => string;
  describeDinnerError: (error: unknown) => string;
}): Promise<DateShiftOutcome> {
  const outcome: DateShiftOutcome = { movedEvents: [], movedDinners: [], failed: [] };
  const fresh = new Map((input.freshItems || []).map((item) => [item.id, item]));
  for (const move of input.events) {
    const current = fresh.get(move.id);
    if (!current) {
      outcome.failed.push({ label: move.label, move, message: 'could not be found any more' });
    } else if (itemChangedSince(move.item, current)) {
      outcome.failed.push({ label: move.label, move, message: EDITED_MEANWHILE });
    } else {
      try {
        await input.saveEvent(move);
        outcome.movedEvents.push(move);
      } catch (error) {
        outcome.failed.push({ label: move.label, move, message: input.describeEventError(error) || 'The save was refused.' });
      }
    }
  }
  for (const move of input.dinners) {
    try {
      await input.saveDinner(move);
      outcome.movedDinners.push(move);
    } catch (error) {
      outcome.failed.push({ label: move.label, move, message: input.describeDinnerError(error) || 'The save was refused.' });
    }
  }
  return outcome;
}

export interface ReplanResult {
  /** Still where the plan found them: re-planned from the fresh read (fresh body, fresh dinner version). */
  events: EventShift[];
  dinners: DinnerShift[];
  /** Already on the new time (the first attempt landed after all): nothing to write, counted as moved. */
  alreadyMoved: DateShiftMove[];
  /** Not where the plan found them, or gone: left alone and named. A second shift would be a double shift. */
  changed: DateShiftFailure[];
}

/**
 * "Try again" for what did not move, from a FRESH read. An item is moved only if its current local time still equals the
 * time the plan started from (`fromWall`): if it now sits on the planned result the first attempt did land, and anything
 * else means someone changed it since, so it is listed rather than shifted. Never a blind repeat of the old request.
 *
 * The comparison is on the ANCHOR time only (an event's start, else its end; a dinner's reservedAt), the one time the plan moved by
 * whole days. An event whose OTHER end was edited meanwhile still moves, from the fresh body, so that edit survives. An event whose
 * anchor was moved by someone else is the case this guards: it is left alone.
 */
export function replanFailed(input: {
  failed: readonly DateShiftMove[];
  items: readonly GroupItineraryItem[] | null | undefined;
  holds: readonly GroupDinnerHold[] | null | undefined;
  days: number;
  tripZone: string;
}): ReplanResult {
  const result: ReplanResult = { events: [], dinners: [], alreadyMoved: [], changed: [] };
  const items = new Map((input.items || []).map((item) => [item.id, item]));
  const holds = new Map((input.holds || []).map((hold) => [hold.id, hold]));
  const changed = (move: DateShiftMove, message = CHANGED_ELSEWHERE) => result.changed.push({ label: move.label, move, message });
  for (const move of input.failed) {
    if (move.kind === 'event') {
      const current = items.get(move.id);
      if (!current) { changed(move, 'no longer exists'); continue; }
      const wall = isoToWallClock(current.startsAt || current.endsAt, move.zone);
      if (wall === move.toWall) { result.alreadyMoved.push(move); continue; }
      const planned = wall === move.fromWall ? planEvent(current, input.days, input.tripZone) : null;
      if (!planned || 'reason' in planned || planned.fromWall !== move.fromWall) changed(move);
      else result.events.push(planned);
    } else {
      const current = holds.get(move.id);
      if (!current) { changed(move, 'no longer exists'); continue; }
      const wall = isoToWallClock(current.reservedAt, move.zone);
      if (wall === move.toWall) { result.alreadyMoved.push(move); continue; }
      const planned = wall === move.fromWall ? planDinner(current, input.days, input.tripZone) : null;
      if (!planned || 'reason' in planned || planned.fromWall !== move.fromWall) changed(move);
      else result.dinners.push(planned);
    }
  }
  return result;
}
